import { useEffect, useState } from 'react';
import logo from '../../assets/pknin-logo.png';
import { isMobileGameDevice } from '../../hooks/useMobileGameViewport';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { switchState } from '../../slices/stateSlice';
import { SiteState } from '../../enums';
import { batch } from 'react-redux';
import { setCharacterData } from '../../slices/characterSlice';
import { setItems } from '../../slices/inventorySlice';
import { setQuests } from '../../slices/questsSlice';
import { setEquippedSkills, setKnownSkills } from '../../slices/skillsSlice';
import { setStats } from '../../slices/statSlice';
import { setDisplayName, setSlotFight, setHomeVillage, setMultiFight } from '../../slices/uiSlice';
import { setArenaStats } from '../../slices/arenaSlice';
import { setServerConfig } from '../../util/serverConfig';
import { setPartyId } from '../../slices/partySlice';
import { LoadState, setId, setLoadState, setServerId, setServerVersion } from '../../slices/accountSlice';
import { setExploration, setHunt } from '../../slices/fieldSlice';
import { setAssetConfig, applyItemOverrides } from '../../util/assetConfig';
import i18n from '../../i18n';
import { cancelFromServer, connectWithJWT, fromServer, toServer } from '../../util/ServerSocket';
import { setStones } from '../../slices/currencySlice';
import {appConnectionStatus,isBundledClient} from '../../util/appConnectionStatus';

export function GameLoader() {
  // Loading uses the phone's real viewport; game scaling starts after entry.
  const [mobile] = useState(isMobileGameDevice);
  const [recovering]=useState(()=>isBundledClient()&&appConnectionStatus.get()==='reconnecting');
  const [progress, setProgress] = useState(10);
  const dispatch = useAppDispatch();
  const loadState = useAppSelector((state) => state.account.loading);

  useEffect(() => {
    document.documentElement.classList.add('pknin-loading-scroll-lock');
    return () => document.documentElement.classList.remove('pknin-loading-scroll-lock');
  }, []);

  useEffect(() => {
    const jwt = localStorage.getItem('jwt');
    const onLoad = (character: any) => {
      if(isBundledClient())appConnectionStatus.ready();
      setProgress(55);
      batch(() => {
        console.log(character);
        dispatch(setArenaStats(character.arena));
        dispatch(setCharacterData(character));
        dispatch(setDisplayName(character.displayName));
        dispatch(setEquippedSkills(character.skills));
        dispatch(setExploration(character.exploration));
        dispatch(setHomeVillage(character.homeVillage));
        dispatch(setHunt(character.hunt));
        dispatch(setId(character.accountId));
        dispatch(setItems(character.items));
        dispatch(setKnownSkills(character.skillsKnown));
        dispatch(setMultiFight(character.multiFight));
        dispatch(setPartyId(character.party));
        dispatch(setQuests(character.quests));
        dispatch(setServerId(character.serverId));
        dispatch(setServerVersion(character.serverVersion));
        dispatch(setSlotFight(character.slotFights));
        dispatch(setStats(character.stats));
        dispatch(setStones(character.stones));
      });

      Object.keys(character.serverConfig).forEach((key) => {
        setServerConfig(key.toUpperCase(), character.serverConfig[key]);
      });

      applyItemOverrides(character.serverConfig.itemOverrides);
      for (const [id, name] of Object.entries(character.serverConfig.titleNames || {})) {
        for (const lang of ['th', 'en', 'vi']) i18n.addResource(lang, 'translation', `title__${id}`, String(name));
      }
      Promise.all([
        fetch(`${process.env.REACT_APP_CDN_PATH}scenes/npcs.json`)
          .then((r) => r.json())
          .then((d) => setAssetConfig('SCENE_NPCS', d))
          .catch((e) => console.error(e)),
        fetch(`json/items.json`)
          .then((r) => r.json())
          .then((d) => setAssetConfig('ITEMS', d))
          .catch((e) => console.error(e)),
      ]).finally(() => { setProgress(70); dispatch(setLoadState(LoadState.LOADED)); });
    };

    fromServer('loadData', onLoad);

    if (jwt === null) {
      dispatch(switchState(SiteState.LANDING));
    } else {
      dispatch(setLoadState(LoadState.LOADING));
      connectWithJWT(jwt);
    }

    return () => {
      cancelFromServer('loadData');
    };
  }, [dispatch]);

  useEffect(() => {
    if (loadState === LoadState.LOADED) {
      const duration = recovering?0:4000 + Math.floor(Math.random() * 6001);
      const started = Date.now();
      const ticker = window.setInterval(() => {
        setProgress(Math.min(99, 70 + Math.floor((Date.now() - started) / duration * 29)));
      }, 100);
      const timer = window.setTimeout(() => {
        setProgress(100);
        dispatch(switchState(SiteState.GAME));
        toServer('sceneGoToCurrent');
      }, duration);
      return () => { window.clearInterval(ticker); window.clearTimeout(timer); };
    }
  }, [loadState, dispatch]);

  return (
    <div className={`ninja-loading${mobile ? '' : ' desktop-welcome'}`} role="status" aria-live="polite" aria-label="กำลังโหลดข้อมูลเกมส์และตัวละคร">
      <div className="mobile-welcome-art" aria-hidden="true" />
      <div className="mobile-welcome-brand">
        <img src={logo} alt="PkNiN" />
        <span>ตำนานนินจาบทใหม่ เริ่มต้นที่นี่</span>
      </div>
      <div className="mobile-welcome-bottom">
        <span className="mobile-welcome-kicker">เตรียมพร้อมออกเดินทางสู่โลกนินจา</span>
        <div className="mobile-welcome-status"><span>กำลังโหลดข้อมูลเกมส์และตัวละคร</span><span>{progress}%</span></div>
        <div className="mobile-welcome-track" role="progressbar" aria-label="ความคืบหน้าการโหลดเกม" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
          <div className="game-entry-loading-fill" style={{ width: `${progress}%` }} />
        </div>
        <small>PkNiN · พีเคนิน</small>
      </div>
    </div>
  );
}
